import {
    useState, useRef, useEffect, useMemo,
} from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';

import Mic from '@mui/icons-material/Mic';
import InfoIcon from '@mui/icons-material/Info';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { marked } from 'marked';
import sanitizeHtml from 'sanitize-html';
import GetAppIcon from '@mui/icons-material/GetApp';
import {
    ClickAwayListener,
    Menu, MenuItem, MenuList, Paper, Popper,
} from '@mui/material';

import { App } from 'BMapsSrc/BMapsApp';
import { captureAndTranscribe } from 'BMapsSrc/util/speech_utils';
import { EventBroker } from '../eventbroker';
import { addEntry } from '../gibbs/conversationHistory';
import CmdInterpreter from '../cmds/CmdInterpreter';
import {
    getPreference, savePreference, loadPreferences,
} from '../gibbs/preferences';
import { formatStatsAsMarkdown } from '../gibbs/analytics';
import { getSuggestions } from '../gibbs/contextualSuggestions';
import { initActivityLog } from '../gibbs/activityLog';
import {
    classifyDomain, getDomainConfig,
    captureInteraction, buildCaptureEntry,
    submitFeedback, getLastCaptureId,
    guardMessage,
} from '../gibbs/cortex';
import { DialogSubmitButton, DialogCancelButton } from './common/DialogActionButtons';
import { WebServices } from '../WebServices';
import { MitigationVisual } from './MitigationVisual';
import { ActiveFrameChip } from './QuestionChooser';
import { WorkingIndicator, downloadData } from './ui_utils';
import { loginUrlForMapCase } from '../connection_utils';
import BMapsTooltip from './BMapsTooltip';
import ResultCard from './ResultCard';

const BotName = 'Gibbs';

/**
 * Parse a "remember this" style preference command from user input.
 * Returns { key, value, confirmText } or null if not matched.
 *
 * @param {string} message
 * @returns {{ key: string, value: *, confirmText: string }|null}
 */
function tryParsePreferenceCommand(message) {
    const lower = message.toLowerCase().trim();

    // Gate: must contain a preference-setting keyword
    const triggers = [
        'remember that', 'remember ',
        'set preference', 'prefer ',
        'always use', 'turn off', 'turn on',
        'be more', 'be brief', 'be concise',
        'be detailed', 'explain more',
        'normal verbosity',
    ];
    if (!triggers.some((t) => lower.startsWith(t)
        || lower.includes(t))) {
        return null;
    }

    // Auto-execute toggles
    if (/turn\s+off\s+auto[- ]?execute/i.test(lower)) {
        return {
            key: 'autoExecuteSingleActions',
            value: false,
            confirmText: 'Auto-execute is now off.',
        };
    }
    if (/turn\s+on\s+auto[- ]?execute/i.test(lower)) {
        return {
            key: 'autoExecuteSingleActions',
            value: true,
            confirmText: 'Auto-execute is now on.',
        };
    }

    // Scoring function preferences
    const scoringMatch = lower.match(
        /prefer\s+(\w+)\s+scoring|(\w+)\s+scoring/
    );
    if (scoringMatch) {
        const fn = (scoringMatch[1] || scoringMatch[2])
            .toUpperCase();
        return {
            key: 'preferredScoringFunctions',
            value: [fn],
            confirmText: `Noted: preferred scoring set to ${fn}.`,
        };
    }

    // Tier preferences
    if (/workflow\s+tier|always\s+use\s+workflow/i.test(lower)) {
        return {
            key: 'defaultTier',
            value: 'workflow',
            confirmText: 'Default tier set to workflow.',
        };
    }
    if (/command\s+tier|always\s+use\s+command/i.test(lower)) {
        return {
            key: 'defaultTier',
            value: 'command',
            confirmText: 'Default tier set to command.',
        };
    }

    // Export format preferences
    if (/markdown\s+export|prefer\s+markdown/i.test(lower)) {
        return {
            key: 'defaultExportFormat',
            value: 'markdown',
            confirmText: 'Default export format set to markdown.',
        };
    }
    if (/json\s+export|prefer\s+json/i.test(lower)) {
        return {
            key: 'defaultExportFormat',
            value: 'json',
            confirmText: 'Default export format set to JSON.',
        };
    }
    if (/csv\s+export|prefer\s+csv/i.test(lower)) {
        return {
            key: 'defaultExportFormat',
            value: 'csv',
            confirmText: 'Default export format set to CSV.',
        };
    }

    // Auto-save workflows
    if (/auto[- ]?save\s+workflow/i.test(lower)) {
        const off = /off|disable|don't|no/i.test(lower);
        return {
            key: 'autoSaveWorkflows',
            value: !off,
            confirmText: off
                ? 'Workflow auto-save is now off.'
                : 'Workflow auto-save is now on.',
        };
    }

    // Verbosity preferences
    if (/be\s+(more\s+)?concise|be\s+brief/i.test(lower)) {
        return {
            key: 'verbosity',
            value: 'concise',
            confirmText: 'Verbosity set to concise.',
        };
    }
    if (/explain\s+more|be\s+(more\s+)?detailed/i.test(lower)) {
        return {
            key: 'verbosity',
            value: 'detailed',
            confirmText: 'Verbosity set to detailed.',
        };
    }
    if (/normal\s+verbosity/i.test(lower)) {
        return {
            key: 'verbosity',
            value: 'normal',
            confirmText: 'Verbosity set to normal.',
        };
    }

    return null;
}

/**
 * Try to parse a compound comparison request and return a
 * Markdown comparison table, or null if not a match.
 *
 * @param {string} message - User input
 * @param {Object} workspace - App.Workspace
 * @returns {string|null}
 */
/**
 * Compound comparison shortcut — "compare compound 1 to compound 3".
 * Uses anonymized getCompoundData() to avoid exposing compound identity.
 *
 * @param {string} message
 * @param {Object} workspace
 * @returns {Promise<string|null>} Formatted comparison or null
 */
async function tryCompoundComparison(message, workspace, knowledgeMod) {
    const k = knowledgeMod || await import('../gibbs/knowledge');
    const { getCompoundData } = await import(
        '../gibbs/text-to-action/serializeWorkspace'
    );

    if (!k.matchComparisonIntent(message)) return null;

    const data = getCompoundData(workspace);
    if (!data || data.length < 2) {
        return 'Need at least two loaded compounds to compare.';
    }

    const indices = k.parseComparisonIndices(message);

    // If user specified two compounds, compare those
    if (indices.length >= 2) {
        const a = data.find((d) => d.id === indices[0]);
        const b = data.find((d) => d.id === indices[1]);
        if (!a || !b) {
            return `Could not find compound ${!a ? indices[0] : indices[1]}.`
                + ` There are ${data.length} compounds loaded (1–${data.length}).`;
        }
        return k.buildComparison(a, b);
    }

    // If only one index or none, compare the top two by score
    const sorted = [...data].sort((x, y) => {
        const sA = x.energies?.interactionScore ?? Infinity;
        const sB = y.energies?.interactionScore ?? Infinity;
        return sA - sB;
    });
    return k.buildComparison(sorted[0], sorted[1]);
}

/**
 * Renders a clarification question with clickable option chips.
 * Used when the server needs disambiguation before executing an action.
 *
 * An option flagged `input: true` (e.g. "Type a protein or PDB ID") renders as
 * an inline text field instead of a click-only chip: on submit it resumes with
 * { ...opt.value, input: <typed> }. This keeps free-text answers on the chip /
 * resume() path (which builds a dispatchable plan) rather than the re-plan path,
 * and fixes the dead-end pill (#143).
 */
function ClarificationMessage({ clarification, onSelect }) {
    const [typed, setTyped] = useState({});

    const submitInput = (opt) => {
        const text = (typed[opt.label] || '').trim();
        if (!text) return;
        onSelect({ ...opt.value, input: text }, text);
    };

    return (
        <Box>
            <Typography>{clarification.question}</Typography>
            <Box sx={{
                display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1,
            }}
            >
                {clarification.options.map((opt) => (opt.input ? (
                    <Box
                        key={opt.label}
                        sx={{
                            display: 'flex', alignItems: 'center', gap: 0.5, width: '100%',
                        }}
                    >
                        <TextField
                            size="small"
                            variant="outlined"
                            placeholder={opt.placeholder || opt.label}
                            value={typed[opt.label] || ''}
                            onChange={(e) => setTyped(
                                (prev) => ({ ...prev, [opt.label]: e.target.value })
                            )}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    submitInput(opt);
                                }
                            }}
                            sx={{ flex: 1 }}
                        />
                        {/* Visible submit button (matches the chat SEND button)
                            so the affordance is discoverable — Enter alone is
                            not obvious to most users. */}
                        <DialogSubmitButton
                            size="small"
                            aria-label="submit target"
                            onClick={() => submitInput(opt)}
                        >
                            Go
                        </DialogSubmitButton>
                    </Box>
                ) : (
                    <Chip
                        key={opt.label}
                        label={opt.label}
                        onClick={() => onSelect(opt.value, opt.label)}
                        clickable
                        title={opt.label}
                        sx={{
                            // Cap each pill at the container width so labels
                            // containing long content (e.g. "oxazole: c1cocn1")
                            // don't overflow horizontally outside the chat
                            // box. Surfaced 2026-05-14 Bill walkthrough.
                            maxWidth: '100%',
                            '& .MuiChip-label': {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            },
                        }}
                    />
                )))}
            </Box>
        </Box>
    );
}

/**
 * Render a confirmation-card param value as a human-readable string.
 *
 * Plain values (string, number, boolean) render directly. Objects are
 * inspected for common BMaps identity fields (name, label, pdbId, smiles,
 * id) and the first found is used; otherwise we fall back to a JSON
 * preview rather than collapsing to "[object Object]" which is what
 * users saw on confirmation cards before 2026-05-14.
 *
 * @param {*} v
 * @returns {string}
 */
function formatParamValue(v) {
    if (v === null || v === undefined) return '';
    const t = typeof v;
    if (t === 'string' || t === 'number' || t === 'boolean') return String(v);
    if (Array.isArray(v)) {
        if (v.length === 0) return '[]';
        if (v.length === 1) return `[ ${formatParamValue(v[0])} ]`;
        return `[ ${v.length} items: ${formatParamValue(v[0])}, ... ]`;
    }
    // Object — try identity fields, then fall back to a JSON preview
    if (v.name) return String(v.name);
    if (v.label) return String(v.label);
    if (v.pdbId) return String(v.pdbId);
    if (v.smiles) return String(v.smiles);
    if (v.id !== undefined) return `id: ${v.id}`;
    try {
        const preview = JSON.stringify(v);
        return preview.length > 80 ? `${preview.slice(0, 77)}...` : preview;
    } catch (_) {
        return '[object]';
    }
}

/**
 * Renders a confirmation card for a single action before execution.
 * Shows what Gibbs will do and lets the user approve, edit, or cancel.
 */
function ActionConfirmation({
    action, onExecute, onCancel, onAlwaysExecute,
}) {
    const params = {
        ...(action.params || {}),
        ...(action.resolvedInputs || {}),
    };
    const paramEntries = Object.entries(params).filter(
        ([, v]) => v !== undefined && v !== null
    );

    return (
        <Box sx={{
            border: '1px solid #ccc',
            borderRadius: 1,
            p: 1.5,
            mt: 1,
            backgroundColor: '#f9f9f9',
        }}
        >
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                {action.actionId}
            </Typography>
            {paramEntries.length > 0 && (
                <Box sx={{ mt: 0.5, fontSize: '0.85rem' }}>
                    {paramEntries.map(([k, v]) => (
                        <div key={k}>
                            <strong>
                                {k}
                                :
                            </strong>
                            {' '}
                            {formatParamValue(v)}
                        </div>
                    ))}
                </Box>
            )}
            <Box sx={{
                display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap',
            }}
            >
                <Chip
                    label="Execute"
                    color="primary"
                    onClick={onExecute}
                    clickable
                    size="small"
                />
                <Chip
                    label="Always Execute"
                    variant="outlined"
                    onClick={onAlwaysExecute}
                    clickable
                    size="small"
                />
                <Chip
                    label="Cancel"
                    variant="outlined"
                    onClick={onCancel}
                    clickable
                    size="small"
                />
            </Box>
        </Box>
    );
}

/** Source attribution icons for cross-domain results (7.4). */
const SOURCE_ICONS = {
    documentation: '\uD83D\uDCC4',
    institutional: '\uD83E\uDDEA',
    graph: '\uD83D\uDD17',
};

/**
 * Render a small source tag for a retrieval result chunk.
 */
function SourceTag({ source }) {
    if (!source || !SOURCE_ICONS[source]) return null;
    const icon = SOURCE_ICONS[source];
    const label = source.charAt(0).toUpperCase() + source.slice(1);
    return (
        <Chip
            size="small"
            label={`${icon} ${label}`}
            sx={{
                mr: 0.5,
                mb: 0.5,
                fontSize: '0.7rem',
                height: 20,
            }}
            variant="outlined"
        />
    );
}

export function ChatBot({ loggedIn, initialMessages: initialMsgs=[] }) {
    const initialMessages = useMemo(
        () => initialMsgs.map((msg, i) => ({ ...msg, id: i })),
        [initialMsgs]
    );
    const [input, setInput] = useState('');
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const [messages, setMessages] = useState(initialMessages);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [recording, setRecording] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const inputRef = useRef(null);

    // --- Group 7: Multi-domain state ---
    const [activeDomain, setActiveDomain] = useState('discovery');
    const [domainMenuAnchor, setDomainMenuAnchor] = useState(null);
    const lastCaptureIdRef = useRef(null);

    // Feature 1: global keyboard shortcuts
    useEffect(() => {
        const handler = (e) => {
            // Ctrl+G / Cmd+G: focus the ChatBot input
            if (e.key === 'g'
                && (e.ctrlKey || e.metaKey)
                && !e.shiftKey && !e.altKey) {
                e.preventDefault();
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }
            // Escape: clear input and dismiss pending
            if (e.key === 'Escape') {
                setInput('');
                if (pendingAction) {
                    setPendingAction(null);
                }
            }
        };
        document.addEventListener('keydown', handler);
        return () => {
            document.removeEventListener('keydown', handler);
        };
    }, [pendingAction]);

    useEffect(() => {
        if (messagesEndRef?.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [messages]);

    useEffect(() => {
        if (initialMessages.length > 0) {
            (async () => {
                try {
                    setIsLoading(true);
                    await submitMessages(initialMessages);
                } catch (sendError) {
                    console.error('Error making chat agent request:', sendError);
                    setError(sendError);
                } finally {
                    setIsLoading(false);
                }
            })();
        }
    }, [initialMessages]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load Gibbs preferences and init activity log on first render (9.4)
    useEffect(() => {
        loadPreferences();
        initActivityLog();
    }, []);

    // Auto-suggest next step after a workflow completes.
    // Subscribes to the workflowComplete event from WorkflowPane.
    useEffect(() => {
        const handler = async (_, data) => {
            try {
                // Branch: if the workflow produced ranked compounds,
                // emit a ResultCard. Otherwise fall back to the
                // nextStepSuggester paragraph. The criterion forbids
                // double-emission, so these branches are exclusive.
                const [
                    { rankCompounds, defaultWeights },
                    { getCompoundData },
                ] = await Promise.all([
                    import('../gibbs/results/compoundRanker'),
                    import('../gibbs/text-to-action/serializeWorkspace'),
                ]);
                const ranked = rankCompounds(
                    getCompoundData(App.Workspace), defaultWeights
                );
                if (ranked.length >= 1) {
                    const card = (
                        <ResultCard
                            ranked={ranked.slice(0, 3)}
                            onOpenPane={undefined}
                        />
                    );
                    setTimeout(() => {
                        // Render JSX inline via the message's `jsx` field.
                        setMessages((oldMessages) => [
                            ...oldMessages,
                            {
                                role: 'assistant',
                                content: '',
                                jsx: card,
                                id: oldMessages.length,
                            },
                        ]);
                    }, 500);
                    return;
                }
                const { suggestNextStep } = await import(
                    '../gibbs/knowledge/nextStepSuggester'
                );
                const suggestion = suggestNextStep(
                    data?.plan, App.Workspace
                );
                if (suggestion) {
                    // Small delay so the suggestion appears AFTER
                    // the Workflow Checker's completion UI renders
                    setTimeout(() => {
                        addBotMessage(suggestion, messages.length + 1);
                    }, 500);
                }
            } catch (_err) {
                // Non-fatal — suggestions are a nice-to-have
            }
        };
        const unsub = EventBroker.subscribe(
            'workflowComplete', handler
        );
        return () => unsub?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Execute a pending single action and update the chat.
     */
    const executePendingAction = async (plan, msgId, userMsg) => {
        setPendingAction(null);
        setIsLoading(true);
        try {
            const execResult = await CmdInterpreter
                .interpretActionPlan(plan);
            if (execResult.type === 'executed'
                && execResult.errors.length === 0) {
                const names = execResult.results
                    .map((r) => r.actionName).join(', ');
                addBotMessage(`Done: ${names}`, msgId);
                // Feature 2: track recent actions
                const actionId = plan.actions[0].actionId;
                const recent = getPreference(
                    'recentActions', []
                );
                const updated = [
                    actionId,
                    ...recent.filter((id) => id !== actionId),
                ].slice(0, 10);
                savePreference('recentActions', updated);
            } else if (execResult.errors.length > 0) {
                addBotMessage(
                    `I tried but ran into an issue: ${
                        execResult.errors.join('; ')
                    }`,
                    msgId
                );
            }
            const actionIds = (plan.actions || [])
                .map((a) => a.actionId);
            addEntry(userMsg, 'plan', actionIds);
        } catch (err) {
            addBotMessage(
                `Error executing action: ${err.message}`, msgId
            );
        } finally {
            setIsLoading(false);
        }
    };

    const myStructure = App.Workspace && App.Workspace.getLoadedProtein();
    const loginUrl = loginUrlForMapCase(myStructure);

    const addBotMessage = (content, idIn, extras) => {
        setMessages((oldMessages) => {
            const id = idIn ?? oldMessages.length;
            const newMessage = { role: 'assistant', content, id, ...(extras || {}) };
            return [...oldMessages, newMessage];
        });
    };

    /**
     * Theme A (Bill walkthrough 2026-05-14): given a set of medchem-advice
     * rules, resolve their `mitigationOptions` against the loaded active
     * compound and return a list of visual descriptors. Returns [] when
     * no compound is loaded, no rule has mitigationOptions, or no
     * descriptor matches — caller renders prose-only in those cases.
     */
    const buildMitigationVisuals = async (rules) => {
        if (!Array.isArray(rules) || rules.length === 0) return [];
        const activeCompound = App.Workspace
            ?.getSelectedOrActiveCompounds?.()?.[0];
        const smiles = activeCompound?.getSmiles?.()
            || activeCompound?.smiles
            || null;
        if (!smiles) return []; // criterion #4: graceful no-compound fallback

        const { resolveMitigationHighlight, getApplicableMitigations } =
            await import('../gibbs/knowledge');

        const visuals = [];
        for (const rule of rules) {
            const opts = typeof getApplicableMitigations === 'function'
                ? getApplicableMitigations(rule, smiles)
                : (rule.mitigationOptions || []);
            for (const opt of opts) {
                try {
                    const r = await resolveMitigationHighlight(
                        rule.id, opt.strategy, smiles
                    );
                    if (r && r.atomIndices.length > 0) {
                        visuals.push({
                            smiles,
                            atomIndices: r.atomIndices,
                            caption: r.caption,
                            colour: r.colour,
                        });
                    }
                } catch (vErr) {
                    // eslint-disable-next-line no-console
                    console.warn(
                        `[Gibbs] mitigation visual error for `
                        + `${rule.id}/${opt.strategy}:`, vErr?.message
                    );
                }
            }
        }
        return visuals;
    };

    const handleSend = async (message=input) => {
        try {
            if (message.trim()) {
                setIsLoading(true);
                const userMessage = { role: 'user', content: message, id: messages.length };
                const updatedMessages = [...messages, userMessage];
                setMessages(updatedMessages);
                setInput('');
                await submitMessages(updatedMessages);
            }
        } catch (sendError) {
            console.error('Error making chat agent request:', sendError);
            setError(sendError);
        } finally {
            setIsLoading(false);
        }
    };

    const handleActionPlanResponse = (responseData, msgId, userMessage) => {
        const { actionPlan } = responseData;
        if (actionPlan.type === 'plan') {
            const actionIds = (actionPlan.plan.actions || []).map((a) => a.actionId);
            const planSummary = actionPlan.plan.summary || '';
            if (actionPlan.plan.intent === 'workflow') {
                // The request travels with the plan so the panel can lead with a
                // short name taken from what was actually asked (#145). It is
                // display state and stays in the browser — the plan's own summary
                // is what gets signed and narrated.
                EventBroker.publish('workflowProposed', {
                    plan: actionPlan.plan,
                    request: userMessage,
                });
                addBotMessage(
                    "I've proposed a workflow. Review it in the Workflow Checker panel.",
                    msgId
                );
                addEntry(userMessage, 'workflow', actionIds, planSummary);
            } else if (actionPlan.plan.intent === 'modify_workflow') {
                EventBroker.publish('workflowModified', {
                    modifications: actionPlan.plan.modifications,
                    summary: actionPlan.plan.summary,
                });
                addBotMessage(
                    actionPlan.plan.summary || "I've updated the workflow.",
                    msgId
                );
                addEntry(userMessage, 'workflow', actionIds, planSummary);
            } else if (actionPlan.plan.intent === 'single_action') {
                addBotMessage('The action was executed.', msgId);
                addEntry(userMessage, 'plan', actionIds, planSummary);
            }
        } else if (actionPlan.type === 'clarification') {
            addClarificationMessage(actionPlan.clarification, msgId);
            addEntry(
                userMessage, 'clarification', [],
                actionPlan.clarification && actionPlan.clarification.question
            );
        } else if (actionPlan.type === 'chat') {
            const chatText = actionPlan.response || responseData.response;
            addBotMessage(chatText, msgId);
            addEntry(userMessage, 'chat', [], chatText);
        }
    };

    const addClarificationMessage = (clarification, idIn) => {
        setMessages((oldMessages) => {
            const id = idIn ?? oldMessages.length;
            const newMessage = {
                role: 'assistant',
                content: clarification.question,
                id,
                clarification,
            };
            return [...oldMessages, newMessage];
        });
    };

    const handleClarificationSelect = async (value, label) => {
        try {
            setIsLoading(true);

            // The displayed user turn must be a string — the raw
            // option value can be an object (e.g. {dockingProgram,
            // speedArg}) which used to white-screen the ChatBot
            // when marked() tried to parse it as markdown.
            const displayText = label
                || (typeof value === 'string'
                    ? value
                    : JSON.stringify(value));
            const userMessage = {
                role: 'user',
                content: displayText,
                id: messages.length,
            };
            setMessages((prev) => [...prev, userMessage]);

            // Resume from the most recent clarification's stored
            // context. Without this, the previous implementation
            // stuffed the raw value into a synthetic user message
            // and re-ran the pipeline, which lost the partial plan
            // and let the LLM pick a completely different action.
            const lastClarif = [...messages]
                .reverse()
                .find((m) => m.clarification);
            const context = lastClarif?.clarification?.context;
            const msgId = messages.length + 1;

            if (!context) {
                addBotMessage(
                    'I lost track of what we were clarifying —'
                    + ' could you rephrase the request?',
                    msgId
                );
                return;
            }

            const { resumeFromClarification: resume } = await import(
                '../gibbs/text-to-action'
            );
            const workspace = App.Workspace || {};
            const {
                plan, needsMoreClarification, chatMessage, rewriteMessage,
            } = resume(context, value, workspace);

            // Chemistry-disambiguation cascade (Theme F, 2026-05-15) returns
            // a rewriteMessage — the user's original prompt with the
            // ambiguous reference replaced by the picked interpretation.
            // Re-run the dispatch chain with the rewritten text so the
            // intent re-resolves cleanly against an unambiguous query.
            if (rewriteMessage) {
                // Show the rewritten message in chat as a system note so
                // the user can see what's about to be dispatched.
                addBotMessage(
                    `Interpreting as: _${rewriteMessage}_`, msgId
                );
                await handleSend(rewriteMessage);
                return;
            }

            // Chat response from the cascade — render as a bot message so
            // markdown in the text actually gets processed, instead of
            // being stuffed into a clarification.question field.
            if (chatMessage) {
                addBotMessage(chatMessage, msgId);
                addEntry(displayText, 'chat', [], chatMessage);
                return;
            }

            if (needsMoreClarification) {
                addClarificationMessage(
                    needsMoreClarification, msgId
                );
                addEntry(
                    displayText, 'clarification', [],
                    needsMoreClarification.question
                );
                return;
            }

            if (plan) {
                await dispatchResumedPlan(
                    plan, msgId, displayText
                );
                return;
            }

            addBotMessage(
                'I could not resume from that choice.', msgId
            );
        } catch (sendError) {
            console.error(
                'Error handling clarification response:', sendError
            );
            setError(sendError);
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Dispatch a plan returned by resumeFromClarification through the
     * same UI flow as a fresh pipeline result.
     */
    const dispatchResumedPlan = async (plan, msgId, userMessageText) => {
        const actionIds = (plan.actions || [])
            .map((a) => a.actionId);

        if (plan.intent === 'single_action') {
            const autoExec = getPreference(
                'autoExecuteSingleActions', false
            );
            if (autoExec) {
                await executePendingAction(
                    plan, msgId, userMessageText
                );
            } else {
                const action = plan.actions[0];
                addBotMessage('I can do this for you:', msgId);
                setPendingAction({
                    plan,
                    msgId: msgId + 1,
                    userMessage: userMessageText,
                    action,
                });
            }
            addEntry(userMessageText, 'plan', actionIds);
            return;
        }

        if (plan.intent === 'workflow') {
            EventBroker.publish('workflowProposed', { plan, request: userMessageText });
            addBotMessage(
                "I've proposed a workflow. Check the"
                + ' Workflow Checker panel on the right.',
                msgId
            );
            addEntry(userMessageText, 'workflow', actionIds);
            return;
        }

        addBotMessage(
            `Resumed plan has unexpected intent "${plan.intent}".`,
            msgId
        );
    };

    const submitMessages = async (sendingMessages) => {
        const lastUserMsg = [...sendingMessages]
            .reverse()
            .find((m) => m.role === 'user');
        const userMessageText = lastUserMsg?.content || '';
        const msgId = sendingMessages.length;

        // Try Gibbs text-to-action pipeline first
        const gibbsResult = await tryGibbsPipeline(
            userMessageText, msgId
        );
        if (gibbsResult) return;

        // Fall back to chat endpoint for conversational responses
        const data = await WebServices.startWsRequestJson(
            WebServices.CHAT_URL,
            { messages: sendingMessages }
        );
        const responseData = JSON.parse(data);
        if (responseData.error === '') {
            setError(null);
            if (responseData.actionPlan) {
                handleActionPlanResponse(
                    responseData, msgId, userMessageText
                );
            } else {
                addBotMessage(responseData.response, msgId);
                addEntry(
                    userMessageText, 'chat', [], responseData.response
                );
                captureKnowledge(
                    userMessageText, 'chat', [],
                    (responseData.response || '').slice(0, 200)
                );
            }
        } else {
            console.error(`ChatBot error: ${responseData.error}`);
            setError(responseData.error);
        }
    };

    /**
     * Try the Gibbs text-to-action pipeline. Returns true if
     * the message was handled (action, workflow, clarification).
     * Returns false if it should fall through to the chat endpoint.
     */
    const tryGibbsPipeline = async (userMessage, msgId) => {
        // --- Policy guard: PII check before sending (7.5) ---
        const guard = guardMessage(userMessage);
        if (!guard.safe) {
            const types = guard.issues
                .map((i) => i.type).join(', ');
            addBotMessage(
                `Warning: potential PII detected (${types}).`
                + ' Please remove sensitive information'
                + ' before sending.',
                msgId
            );
            return true;
        }

        // --- Domain classification (7.1) ---
        const domainResult = classifyDomain(
            userMessage, { selectedDomain: activeDomain }
        );
        if (domainResult.domain !== activeDomain) {
            setActiveDomain(domainResult.domain);
        }

        // Feature 1: instant preference commands
        const prefCmd = tryParsePreferenceCommand(userMessage);
        if (prefCmd) {
            savePreference(prefCmd.key, prefCmd.value);
            addBotMessage(prefCmd.confirmText, msgId);
            addEntry(userMessage, 'plan', []);
            return true;
        }

        // Feature 2: session analytics shortcut
        if (/^(show\s+stats|session\s+analytics|my\s+activity)$/i
            .test(userMessage.trim())) {
            addBotMessage(formatStatsAsMarkdown(), msgId);
            addEntry(userMessage, 'plan', []);
            return true;
        }

        // Pre-load knowledge module once for all shortcut checks.
        // Avoids 5 separate dynamic imports of the same module.
        const knowledge = await import('../gibbs/knowledge');

        // Telemetry log shortcut: "show gibbs log", "show llm log", etc.
        try {
            if (knowledge.matchTelemetryIntent(userMessage)) {
                const { getLLMLog } = await import('../gibbs/telemetry');
                const entries = await getLLMLog(10);
                addBotMessage(
                    knowledge.formatTelemetryLog(entries), msgId
                );
                addEntry(userMessage, 'plan', []);
                return true;
            }
        } catch (telErr) {
            // Non-fatal — fall through
            // eslint-disable-next-line no-console
            console.warn('[Gibbs] Telemetry shortcut error:', telErr);
        }

        // Compound comparison: "compare compound 1 to compound 3"
        //
        // gibbs-compound-compare-ui (2026-05-15): if the user named two
        // compound IDs explicitly, open the CompoundComparePane pre-
        // populated with those IDs. Otherwise fall back to the legacy
        // chat-prose comparison so existing "compare the compounds" /
        // "side by side" phrases still produce useful output without
        // a pane open.
        try {
            if (knowledge.matchComparisonIntent(userMessage)) {
                const indices = knowledge.parseComparisonIndices(userMessage);
                if (indices.length >= 2) {
                    const { CompoundComparePaneEvents } = await import(
                        './dialog/CompoundComparePane/constants'
                    );
                    EventBroker.publish(
                        CompoundComparePaneEvents.OPEN_WITH,
                        { compoundIds: [indices[0], indices[1]] }
                    );
                    addBotMessage(
                        `Opened compare view for compound #${indices[0]} `
                        + `vs #${indices[1]}.`,
                        msgId
                    );
                    addEntry(userMessage, 'ui', ['compound-compare']);
                    return true;
                }
            }

            const compResult = await tryCompoundComparison(
                userMessage, App.Workspace, knowledge
            );
            if (compResult) {
                addBotMessage(compResult, msgId);
                addEntry(userMessage, 'data', []);
                return true;
            }
        } catch (compErr) {
            // Non-fatal — fall through
            // eslint-disable-next-line no-console
            console.warn('[Gibbs] Comparison shortcut error:', compErr);
        }

        // Interaction view shortcut
        try {
            if (knowledge.matchInteractionViewIntent(userMessage)) {
                const setup = knowledge.buildInteractionViewSetup();
                const [{ UserActions: UA }, { HBondStates: HBS }] = await Promise.all([
                    import('BMapsCmds'),
                    import('../model/DisplayState'),
                ]);
                for (const da of setup.actions) {
                    const actionArgs = da.args.map((arg) => {
                        if (arg === 'normal') return HBS.normal;
                        return arg;
                    });
                    if (UA[da.action]) {
                        UA[da.action](...actionArgs);
                    }
                }
                addBotMessage(setup.message, msgId);
                addEntry(userMessage, 'display', []);
                return true;
            }
        } catch (viewErr) {
            // eslint-disable-next-line no-console
            console.warn('[Gibbs] Interaction view shortcut error:', viewErr);
        }

        // Compound data shortcut: "rank by energy", "show scores",
        // "filter by MW under 500", "which compound scored best?".
        try {
            if (knowledge.matchCompoundDataIntent(userMessage)) {
                const { getCompoundData } = await import(
                    '../gibbs/text-to-action/serializeWorkspace'
                );
                const data = getCompoundData(App.Workspace);

                const filter = knowledge.parseFilterExpression(userMessage);
                let response;
                if (filter) {
                    response = knowledge.applyFilter(data, filter);
                } else {
                    const result = knowledge.buildCompoundSummary(data);
                    response = result.text || result;
                }

                // Open the Compound Table for a detailed view
                try {
                    const [reactMod, ctspMod, cmdsMod] = await Promise.all([
                        import('react'),
                        import('BMapsUI/tables/CompoundTableSidePanel'),
                        import('BMapsCmds'),
                    ]);
                    const ce = reactMod.createElement;
                    const CTSP = ctspMod.CompoundTableSidePanel;
                    const UA2 = cmdsMod.UserActions;
                    UA2.OpenSidePanel('left', {
                        pageId: 'compoundTable',
                        component: ce(CTSP),
                        longLived: false,
                    });
                } catch (_) {
                    // Fallback silently if table import fails
                }

                addBotMessage(response, msgId);
                addEntry(userMessage, 'data', []);
                return true;
            }

            // "Open compound table" — tight match
            if (/open.*compound.*table|compound\s+table/i
                .test(userMessage)) {
                try {
                    const [rMod, ctMod, cMod] = await Promise.all([
                        import('react'),
                        import('BMapsUI/tables/CompoundTableSidePanel'),
                        import('BMapsCmds'),
                    ]);
                    cMod.UserActions.OpenSidePanel('left', {
                        pageId: 'compoundTable',
                        component: rMod.createElement(
                            ctMod.CompoundTableSidePanel
                        ),
                        longLived: false,
                    });
                    addBotMessage('Opened the Compound Table.', msgId);
                } catch (_) {
                    const { UserActions: UA3b } = await import('BMapsCmds');
                    UA3b.ShowInspectorTab('energies');
                    addBotMessage('Opened the energy display panel.', msgId);
                }
                addEntry(userMessage, 'display', []);
                return true;
            }
        } catch (dataErr) {
            // eslint-disable-next-line no-console
            console.warn('[Gibbs] Compound data shortcut error:', dataErr);
        }

        // Scan structural alerts shortcut: "scan this compound for
        // toxicophores", "check for structural alerts", "does this have
        // reactive metabolite liabilities". Actually fires the scan against
        // the active compound(s). MUST come BEFORE the advice shortcut so
        // execute-intent queries don't get intercepted by informational
        // match.
        try {
            if (knowledge.matchScanAlertsIntent(userMessage)) {
                const compounds = App.Workspace
                    ?.getSelectedOrActiveCompounds?.() || [];
                if (compounds.length === 0) {
                    addBotMessage(
                        'Load or select a compound first, then I can scan it for'
                        + ' structural alerts (toxicophores).',
                        msgId
                    );
                    addEntry(userMessage, 'chat', []);
                    return true;
                }
                addBotMessage(
                    'Scanning for structural alerts (Meanwell toxicophores)…',
                    msgId
                );
                const { UserActions: UA } = await import('BMapsCmds');
                const result = await UA.ScanStructuralAlerts();
                addBotMessage(result.summary, msgId + 1);
                addEntry(
                    userMessage, 'plan', ['scan-structural-alerts']
                );
                return true;
            }
        } catch (scanErr) {
            // eslint-disable-next-line no-console
            console.warn('[Gibbs] Scan alerts shortcut error:', scanErr);
            addBotMessage(
                'Structural alert scan ran into an error: '
                + `${scanErr.message || scanErr}`,
                msgId
            );
            addEntry(userMessage, 'error', []);
            return true;
        }

        // Medchem advice shortcut: "what isostere for carboxylic acid",
        // "block CYP metabolism with fluorine", "avoid aniline toxicophore",
        // "pick a heteroaromatic ring". Returns Meanwell-cited rules with
        // mitigation strategies — purely informational, zero LLM tokens.
        try {
            const advice = knowledge.tryMedchemAdviceShortcut(userMessage);
            if (advice && advice.handled) {
                // Theme A (Bill walkthrough 2026-05-14): when the advice
                // includes rules with `mitigationOptions` AND a compound
                // is loaded, attach 2D structure visuals with the affected
                // atoms highlighted. The resolver runs asynchronously and
                // skips silently when the (ruleId, strategy) pair has no
                // descriptor or RDKit-WASM is unavailable, so prose
                // continues to render even if visuals fail.
                const visuals = await buildMitigationVisuals(advice.rules);
                addBotMessage(advice.response, msgId,
                    visuals.length > 0 ? { visuals } : null);
                addEntry(userMessage, 'chat', []);
                return true;
            }
        } catch (adviceErr) {
            // eslint-disable-next-line no-console
            console.warn('[Gibbs] Medchem advice shortcut error:', adviceErr);
        }

        // Chemistry-disambiguation detector (Theme F, Bill Kinney walkthrough
        // 2026-05-14). Catches messages that reference ambiguous chemistry
        // terms ("the alpha hydrogen" on a compound with both an amine and
        // a carbonyl). When ambiguous on the active compound, surface a
        // clarification card instead of guessing or burning LLM tokens.
        // Must sit AFTER medchem-advice (so "what isostere for COOH" isn't
        // intercepted) and BEFORE the LLM tier (so it gates ambiguous
        // execute-intent messages).
        try {
            const activeCompound = App.Workspace
                ?.getSelectedOrActiveCompounds?.()?.[0];
            const activeSmiles = activeCompound?.getSmiles?.()
                || activeCompound?.smiles
                || null;
            if (activeSmiles) {
                const detection = knowledge.detectChemistryAmbiguity(
                    userMessage, activeSmiles
                );
                if (detection) {
                    const clar = knowledge.buildChemistryClarification(
                        detection, userMessage
                    );
                    if (clar?.intent === 'chat') {
                        addBotMessage(clar.message, msgId);
                        addEntry(userMessage, 'chat', []);
                        return true;
                    }
                    if (clar?.intent === 'clarify') {
                        addClarificationMessage(clar, msgId);
                        addEntry(
                            userMessage, 'clarification', [], clar.question
                        );
                        return true;
                    }
                }
            }
        } catch (disambigErr) {
            // eslint-disable-next-line no-console
            console.warn(
                '[Gibbs] chemistry-disambiguation shortcut error:',
                disambigErr
            );
            // Non-fatal — fall through to the rest of the chain.
        }

        // Fragment grow shortcut
        try {
            if (knowledge.matchFragmentGrowIntent(userMessage)) {
                const precondCheck = knowledge.checkFragmentGrowPreconditions(
                    App.Workspace
                );
                if (!precondCheck.ready) {
                    addBotMessage(precondCheck.message, msgId);
                    addEntry(userMessage, 'chat', []);
                    return true;
                }

                addBotMessage(
                    'Searching for fragment modifications on the '
                    + 'active compound. This may take a minute...',
                    msgId
                );

                const { UserActions: UA } = await import('BMapsCmds');
                const compounds = App.Workspace
                    .getSelectedOrActiveCompounds();

                const { results } = await UA.GrowFromAllAtoms(
                    compounds,
                    undefined, // default atom filter — all eligible
                    { maxSuggestions: 1, minimize: false }
                );

                const successCount = (results || []).filter(
                    (r) => r.newCompound
                ).length;
                const errorCount = (results || []).filter(
                    (r) => r.errors && r.errors.length > 0
                ).length;

                let summary;
                if (successCount > 0) {
                    summary = `Fragment growing complete. **${successCount}** new `
                        + 'compound(s) created from fragment modifications. '
                        + 'Check the Compounds panel to see the results.\n\n'
                        + 'To evaluate the new compounds, type '
                        + '**"dock and score"** to run them through the '
                        + 'scoring pipeline.';
                } else {
                    summary = 'Fragment growing finished but no modifications'
                        + ' were found. This can happen when the compound has'
                        + ' no eligible bond vectors near fragment hotspots,'
                        + ' or when the fragment library has no compatible'
                        + ' matches for the available grow positions.';
                    if (errorCount > 0) {
                        summary += ` (${errorCount} search errors occurred)`;
                    }
                }

                addBotMessage(summary, msgId + 1);
                addEntry(
                    userMessage, 'plan',
                    ['grow-from-all-atoms']
                );
                return true;
            }
        } catch (growErr) {
            // eslint-disable-next-line no-console
            console.warn(
                '[Gibbs] Fragment grow shortcut error:', growErr
            );
            addBotMessage(
                'Fragment growing ran into an error: '
                + `${growErr.message || growErr}`,
                msgId
            );
            addEntry(userMessage, 'error', []);
            return true;
        }

        try {
            const { textToAction } = await import(
                '../gibbs/text-to-action'
            );
            const workspace = App.Workspace || {};
            const { Loader } = await import('../Loader');
            const result = await textToAction(userMessage, workspace, {
                autonomousCampaignEnabled: Loader.AutonomousCampaignEnabled,
            });

            if (result.type === 'plan') {
                const { plan } = result;
                const actionIds = (plan.actions || [])
                    .map((a) => a.actionId);

                if (plan.intent === 'workflow') {
                    // Multi-step workflow → Workflow Checker panel
                    EventBroker.publish(
                        'workflowProposed', { plan, request: userMessage }
                    );
                    addBotMessage(
                        "I've proposed a workflow. Check the"
                        + ' Workflow Checker panel on the right.',
                        msgId
                    );
                    addEntry(userMessage, 'workflow', actionIds);
                    return true;
                }

                if (plan.intent === 'single_action') {
                    // Check if this is a lightweight action that
                    // should execute instantly without confirmation.
                    // Display/UI actions (runtime: 'interactive')
                    // are non-destructive, instant, and reversible —
                    // asking "are you sure you want to show sticks?"
                    // is a bad user experience.
                    const actionId = plan.actions[0]?.actionId;
                    let isInteractive = false;
                    try {
                        const { getAction: ga } = await import(
                            '../gibbs/registry'
                        );
                        const def = ga(actionId);
                        isInteractive = def?.runtime === 'interactive';
                    } catch (_) { /* fallback to preference-based */ }

                    const autoExec = isInteractive || getPreference(
                        'autoExecuteSingleActions', false
                    );
                    if (autoExec) {
                        // Auto-execute mode — run immediately
                        await executePendingAction(
                            plan, msgId, userMessage
                        );
                    } else {
                        // Confirmation mode — show action card
                        const action = plan.actions[0];
                        addBotMessage(
                            'I can do this for you:',
                            msgId
                        );
                        setPendingAction({
                            plan,
                            msgId: msgId + 1,
                            userMessage,
                            action,
                        });
                    }
                    return true;
                }

                if (plan.intent === 'modify_workflow') {
                    EventBroker.publish('workflowModified', {
                        modifications: plan.modifications,
                        summary: plan.summary,
                    });
                    addBotMessage(
                        plan.summary || "I've updated the workflow.",
                        msgId
                    );
                    addEntry(userMessage, 'workflow', actionIds);
                    return true;
                }
            }

            if (result.type === 'clarification') {
                addClarificationMessage(
                    result.clarification, msgId
                );
                addEntry(userMessage, 'clarification', []);
                return true;
            }

            if (result.type === 'invalid') {
                console.warn(
                    '[Gibbs] Plan rejected by validation:',
                    result.errors,
                    'plan:', result.plan
                );
                const errSummary = (result.errors || [])
                    .slice(0, 3).join('; ');
                addBotMessage(
                    'I drafted a workflow but it failed validation: '
                    + `${errSummary}. You may need to set up the`
                    + ' workspace (load a compound/protein) first.',
                    msgId
                );
                addEntry(userMessage, 'invalid', []);
                return true;
            }

            // 2026-06-02 (G3 RCA fix, cause #3): differentiate shortcut-tier
            // chat results from LLM-tier chat results. Shortcut-tier chats
            // are crafted fallback hints — they explain why preconditions
            // aren't met for a recognized intent (e.g., "Hit-to-lead
            // iterates on early hits... Load a protein and at least one
            // compound, then Gibbs can run the canonical pipeline"). These
            // were previously dropped by the unconditional fall-through to
            // CHAT_URL, replacing the targeted hint with the generic LLM
            // walkthrough plus the "outside the scope of current
            // documentation" disclaimer. Now: emit shortcut chats directly
            // to the user. LLM-tier chats keep the old behavior — fall
            // through to the server endpoint for richer conversational
            // context.
            if (result.type === 'chat' && result.chatSource === 'shortcut') {
                addBotMessage(result.message, msgId);
                addEntry(userMessage, 'chat', []);
                return true;
            }

            if (result.type === 'chat') {
                console.warn(
                    '[Gibbs] LLM classified as chat, not workflow.',
                    'message:', result.message
                );
            }

            // LLM-tier chat (and any unmarked chat result) falls through
            // to the server endpoint for richer conversational context.
            return false;
        } catch (pipelineError) {
            // Pipeline failed — fall through to chat endpoint
            console.warn(
                '[Gibbs] Pipeline error, falling back to chat:',
                pipelineError.message
            );
            return false;
        }
    };

    /**
     * Fire-and-forget knowledge capture after any pipeline response.
     */
    const captureKnowledge = (
        userMsg, responseType, actionIds, summary
    ) => {
        const entry = buildCaptureEntry(
            userMsg, responseType, activeDomain, actionIds, summary
        );
        captureInteraction(entry).then(() => {
            lastCaptureIdRef.current = getLastCaptureId();
        });
    };

    /**
     * Submit feedback for the most recent captured interaction (7.5).
     */
    const handleFeedback = (feedback) => {
        const captureId = lastCaptureIdRef.current;
        if (captureId) {
            submitFeedback(captureId, feedback);
        }
    };

    const handleInputChange = (e) => {
        setInput(e.target.value);
    };

    const handleEnterSubmit = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleClearChat = () => {
        setError(null);
        setMessages([]);
    };

    const handleRegenerate = () => {
        const lastMessage = messages[messages.length - 1];
        const userText = lastMessage.content;
        setInput(userText);
        setError(null);
    };

    function displayChatMessage(inputText) {
        // Defensive: marked() crashes the whole ChatBot if handed a
        // non-string, which white-screens the app. Other code paths
        // (clarification round-trips, malformed server responses)
        // can let objects slip into message.content, so coerce here.
        let text;
        if (typeof inputText === 'string') {
            text = inputText;
        } else if (inputText == null) {
            text = '';
        } else {
            text = JSON.stringify(inputText);
        }
        const renderer = new marked.Renderer();
        function convertLink(href, title, linkText) {
            const link = marked.Renderer.prototype.link.call(this, href, title, linkText);
            // Our ExternalLink also includes a rel="noreferrer" attribute, which excludes
            // the referrer (BMaps) from the request headers.
            // I chose not to add that here. If we decide to add it, we'll need to pass
            // an allowedAttributes option to sanitizeHtml, so rel isn't filtered out.
            return link.replace('<a ', '<a target="_blank" ');
        }
        renderer.link = convertLink;
        marked.setOptions({ renderer });
        return (
            <div
                dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(marked.parse(text), {
                        allowedTags: sanitizeHtml.defaults.allowedTags.concat(['img']),
                        allowedAttributes: {
                            ...sanitizeHtml.defaults.allowedAttributes,
                            img: ['src', 'alt', 'title'],
                        },
                        allowedSchemes: ['https'],
                        // Mitigation for CVE-2026-44990 (sanitize-html ≤ 2.17.3
                        // XSS bypass via <xmp> raw-text passthrough). With the
                        // default 'discard' mode, content inside disallowed
                        // <xmp> tags leaks into the output unescaped and can
                        // smuggle <script> payloads through sanitization.
                        // 'completelyDiscard' drops the disallowed tag AND its
                        // contents, closing that path. No upstream patch is
                        // available yet (first_patched_version: null as of
                        // 2026-05-14). Revisit when sanitize-html publishes a
                        // fix and either roll back or layer with it.
                        disallowedTagsMode: 'completelyDiscard',
                    }),
                }}
            />
        );
    }

    async function captureAndSend() {
        setRecording(true);
        const recorder = await captureAndTranscribe({
            setMediaRecorder,
            handleTranscription: async (words) => {
                if (words != null && words.length > 0) {
                    setInput(words);
                    handleSend(words);
                } else {
                    addBotMessage("If you just said something, I couldn't make it out. Please try again.");
                }
            },
        });
        recorder.onstop = () => setRecording(false);
        setMediaRecorder(recorder);
    }

    async function stopRecording() {
        mediaRecorder.stop();
    }

    return (
        <div style={{
            padding: '1rem',
            paddingTop: '0.5rem',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: '300px',
            flexGrow: 1,
            borderTop: '2px solid lightgray',
        }}
        >
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 0.5,
            }}
            >
                <Typography style={{
                    fontWeight: 'bold', fontSize: 'smaller',
                }}
                >
                    Chat with
                    {' '}
                    {BotName}
                    , our AI assistant:
                </Typography>
                {/* Domain selector. Chip + Menu pattern — two discrete
                    components so the colored pill and the dropdown menu
                    cannot double-render. Earlier attempts with MUI
                    Select (renderValue or backgroundColor variants)
                    produced a ghost second "Discovery" label in some
                    renders; this pattern avoids that class entirely. */}
                <Chip
                    label={getDomainConfig(activeDomain).label}
                    size="small"
                    onClick={(e) => setDomainMenuAnchor(e.currentTarget)}
                    onDelete={(e) => setDomainMenuAnchor(e.currentTarget)}
                    deleteIcon={<ArrowDropDownIcon />}
                    sx={{
                        backgroundColor:
                            getDomainConfig(activeDomain).color,
                        color: '#fff',
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                        height: 24,
                        cursor: 'pointer',
                        '& .MuiChip-deleteIcon': {
                            color: '#fff',
                            marginLeft: '-4px',
                            marginRight: '2px',
                        },
                        '&:hover': { opacity: 0.85 },
                    }}
                    data-testid="domain-selector"
                />
                <Menu
                    anchorEl={domainMenuAnchor}
                    open={Boolean(domainMenuAnchor)}
                    onClose={() => setDomainMenuAnchor(null)}
                >
                    {['discovery', 'clinical', 'corporate'].map((d) => (
                        <MenuItem
                            key={d}
                            selected={d === activeDomain}
                            onClick={() => {
                                setActiveDomain(d);
                                setDomainMenuAnchor(null);
                            }}
                        >
                            {getDomainConfig(d).label}
                        </MenuItem>
                    ))}
                </Menu>
            </Box>
            <Typography style={{
                fontSize: 'small', marginBottom: '0.5rem',
            }}
            >
                Please be aware responses from
                {' '}
                {BotName}
                {' '}
                may not always be accurate.
                Use discretion when interpreting results.
            </Typography>
            <div style={{
                flexGrow: '1',
                overflow: 'auto',
                padding: '0.5rem',
                borderTop: '1px solid lightgray',
                borderLeft: '1px solid lightgray',
                borderRight: '1px solid lightgray',
                borderBottom: 'none',
                borderRadius: '4px 4px 0 0',
                background: 'whitesmoke',
            }}
            >
                {messages.map((message, messageI) => (
                    <Box
                        key={`message_${messageI.toString()}`}
                        ref={message.id === messages.length - 1 ? messagesEndRef : null}
                        style={{
                            marginBottom: '2px',
                            padding: '2px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: message.role === 'user' ? 'flex-end' : 'flex-start',
                        }}
                    >
                        <strong>{message.role === 'user' ? 'You: ' : `${BotName}: `}</strong>
                        <Box sx={{
                            border: '1px solid lightgray',
                            borderRadius: '.3rem',
                            maxWidth: '90%',
                            wordWrap: 'break-word',
                            background: message.role === 'user' ? 'white' : 'mintcream',
                            boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.2)',
                            '& li': {
                                marginLeft: '1rem',
                            },
                            '& div': {
                                padding: '.3rem',
                            },
                            '& pre': {
                                whiteSpace: 'pre-wrap',
                            },
                            '& img': {
                                maxWidth: '100%',
                                height: 'auto',
                            },
                        }}
                        >
                            {/* 7.4 Source attribution tags */}
                            {message.sources && (
                                <Box sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    px: 0.5,
                                    pt: 0.5,
                                }}
                                >
                                    {message.sources.map((s) => (
                                        <SourceTag
                                            key={`src-${s.id || s.title || s.url || JSON.stringify(s)}`}
                                            source={s}
                                        />
                                    ))}
                                </Box>
                            )}
                            {/* When a clarification is attached, render only the
                                clarification component — otherwise the question
                                text appears twice (once via displayChatMessage
                                on message.content, once via ClarificationMessage's
                                Typography on clarification.question). */}
                            {!message.clarification && message.jsx}
                            {!message.clarification && !message.jsx
                                && displayChatMessage(message.content)}
                            {/* Theme A (Bill walkthrough 2026-05-14):
                                attach 2D structure visuals with atom
                                highlights alongside prose mitigation
                                advice. Backwards compatible — messages
                                without `visuals` render unchanged. */}
                            {!message.clarification
                                && Array.isArray(message.visuals)
                                && message.visuals.map((v, vi) => (
                                    <MitigationVisual
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={`vis-${message.id}-${vi}`}
                                        smiles={v.smiles}
                                        atomIndices={v.atomIndices}
                                        caption={v.caption}
                                        colour={v.colour}
                                    />
                                ))}
                            {message.clarification && (
                                <ClarificationMessage
                                    clarification={message.clarification}
                                    onSelect={handleClarificationSelect}
                                />
                            )}
                            {/* 7.5 Feedback buttons */}
                            {message.role === 'assistant'
                                && !message.clarification && (
                                <Box sx={{
                                    display: 'flex',
                                    gap: 0.5,
                                    px: 0.5,
                                    pb: 0.3,
                                }}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={
                                            () => handleFeedback('up')
                                        }
                                        title="Helpful"
                                        sx={{ p: 0.3 }}
                                    >
                                        <ThumbUpIcon
                                            sx={{ fontSize: 14 }}
                                        />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={
                                            () => handleFeedback('down')
                                        }
                                        title="Not helpful"
                                        sx={{ p: 0.3 }}
                                    >
                                        <ThumbDownIcon
                                            sx={{ fontSize: 14 }}
                                        />
                                    </IconButton>
                                </Box>
                            )}
                        </Box>
                    </Box>
                ))}
                <Box>
                    {isLoading && (
                        <Box>
                            <WorkingIndicator />
                        </Box>
                    )}
                </Box>
                {pendingAction && (
                    <Box sx={{
                        p: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                    }}
                    >
                        <strong>
                            {`${BotName}: `}
                        </strong>
                        <ActionConfirmation
                            action={pendingAction.action}
                            onExecute={() => executePendingAction(
                                pendingAction.plan,
                                pendingAction.msgId,
                                pendingAction.userMessage
                            )}
                            onCancel={() => {
                                setPendingAction(null);
                                addBotMessage(
                                    'Action cancelled.',
                                    pendingAction.msgId
                                );
                            }}
                            onAlwaysExecute={() => {
                                savePreference(
                                    'autoExecuteSingleActions',
                                    true
                                );
                                executePendingAction(
                                    pendingAction.plan,
                                    pendingAction.msgId,
                                    pendingAction.userMessage
                                );
                                addBotMessage(
                                    'Got it — I\'ll auto-execute'
                                    + ' single actions from now on.'
                                    + ' You can change this in'
                                    + ' preferences.',
                                    pendingAction.msgId + 1
                                );
                            }}
                        />
                    </Box>
                )}
                {error && <ChatBotErrorDisplay error={error} onTryAgain={handleRegenerate} />}
                <Box>
                    {!loggedIn && (
                        <Typography style={{ fontStyle: 'italic', fontWeight: 'bold', fontSize: 'smaller' }}>
                            Please
                            {' '}
                            <a href={loginUrl}>Log in</a>
                            {' '}
                            to interact with
                            {' '}
                            {BotName}
                            .
                        </Typography>
                    )}
                </Box>
            </div>
            {/* Starter-suggestion chips sit directly above the input so
                they don't visually interrupt the Gibbs message area.
                Kept outside the messages div (which is flexGrow:1) so
                it doesn't bifurcate that area into two gray regions. */}
            {!isLoading && messages.length <= 1 && (
                <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    px: 1,
                    py: 0.75,
                    borderLeft: '1px solid lightgray',
                    borderRight: '1px solid lightgray',
                    background: 'whitesmoke',
                    flexShrink: 0,
                }}
                >
                    {getSuggestions(App.Workspace, activeDomain).map((s) => (
                        <Chip
                            key={s.text}
                            label={s.text}
                            onClick={() => handleSend(s.text)}
                            clickable
                            size="small"
                            variant="outlined"
                        />
                    ))}
                </Box>
            )}
            <TextField
                inputRef={inputRef}
                style={{
                    marginBottom: '0.5rem',
                    width: '100%',
                    flexShrink: 0,
                    borderLeft: '1px solid lightgray',
                    borderRight: '1px solid lightgray',
                    borderRadius: '0 0 4px 4px',
                    background: 'whitesmoke',
                }}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleEnterSubmit}
                placeholder="Enter message..."
                multiline
                maxRows={6}
                size="small"
                disabled={!loggedIn}
                slotProps={{
                    input: {
                        sx: {
                            background: 'white',
                        },
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={recording ? stopRecording : captureAndSend}
                                    style={{
                                        color: recording ? 'green' : undefined,
                                        padding: '2px 4px',
                                        height: 'fit-content',
                                        width: 'fit-content',
                                        display: 'flex',
                                    }}
                                    title={recording ? 'Disable voice control' : 'Enable voice control'}
                                    disabled={!loggedIn}
                                >
                                    <Mic />
                                </IconButton>
                            </InputAdornment>
                        ),
                    },
                }}
            />
            <Box sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                px: 0.5,
                pt: 0.25,
            }}>
                {/* gibbs-question-chooser: small frame indicator;
                    click to open the picker dialog. */}
                <ActiveFrameChip />
            </Box>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <DialogSubmitButton
                    onClick={() => handleSend()}
                    disabled={!loggedIn}
                >
                    Send
                </DialogSubmitButton>
                <DialogCancelButton
                    onClick={handleClearChat}
                    disabled={!loggedIn}
                >
                    Clear chat
                </DialogCancelButton>
                <ChatMenu messages={messages} />
            </div>
        </div>
    );
}

function ChatBotErrorDisplay({ error, onTryAgain }) {
    const errorRef = useRef(null);
    useEffect(() => {
        if (errorRef?.current) {
            errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [error]);

    return (
        <Box ref={errorRef}>
            <strong>
                {`${BotName}: `}
            </strong>
            <BMapsTooltip title={`Error detail: ${error}`}>
                <div style={{ fontStyle: 'italic', color: 'red' }}>
                    Something went wrong...
                    {' '}
                    <InfoIcon fontSize="small" style={{ verticalAlign: 'middle' }} />
                </div>
            </BMapsTooltip>
            <DialogCancelButton style={{ paddingTop: 0, paddingBottom: 0 }} fontSize="smaller" type="button" onClick={onTryAgain}>Try again</DialogCancelButton>
        </Box>
    );
}

function ChatMenu({ messages }) {
    const [open, setOpen] = useState(false);
    const menuRef = useRef(null);
    return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            {messages.length > 0 && (
                <>
                    <BMapsTooltip title="Download chat history">
                        <IconButton size="small" ref={menuRef} onClick={() => setOpen(!open)}>
                            <GetAppIcon />
                        </IconButton>
                    </BMapsTooltip>
                    <Popper
                        open={open}
                        anchorEl={menuRef.current}
                        placement="bottom-start"
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={() => setOpen(false)}>
                                <MenuList>
                                    <MenuItem
                                        sx={{ justifyContent: 'center' }}
                                        onClick={
                                    () => {
                                        const stateString = JSON.stringify(
                                            { chatMessages: messages }
                                        );
                                        downloadData(getChatFileName('json'), stateString, 'application/json');
                                        setOpen(false);
                                    }
                                }
                                    >
                                        JSON
                                    </MenuItem>
                                    <MenuItem
                                        sx={{ justifyContent: 'end' }}
                                        onClick={
                                        () => {
                                            const lines = messages.map((m) => {
                                                const role = m.role === 'user'
                                                    ? 'You' : BotName;
                                                return `**${role}:** ${m.content}`;
                                            });
                                            const md = `# Gibbs Conversation\n> ${new Date().toISOString()}\n\n${lines.join('\n\n')}`;
                                            downloadData(
                                                getChatFileName('md'),
                                                md,
                                                'text/plain'
                                            );
                                            setOpen(false);
                                        }
                                    }
                                    >
                                        Markdown
                                    </MenuItem>
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Popper>
                </>
            )}
        </Box>
    );
}

function getChatFileName(format) {
    return `BMaps_GibbsChat_${formatDate()}.${format}`;
}

/**
 * @param {Date?} dateIn
 * @returns {string}
 */
function formatDate(dateIn) {
    const date = dateIn || new Date();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    // const hours = date.getHours();
    // const minutes = date.getMinutes();
    // const seconds = date.getSeconds();
    return `${year}-${month}-${day}`;
}
